import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, CircleNotch } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { loadRazorpay } from "../lib/razorpay";
import IndexStrip from "../components/IndexStrip";
import Seo from "../components/Seo";
import PricingHeader from "../components/pricing/PricingHeader";
import PricingCard from "../components/pricing/PricingCard";

export default function Pricing() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [plan, setPlan] = useState(null);
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [agreed, setAgreed] = useState(false);

  const load = useCallback(async () => {
    try {
      const [p, m] = await Promise.all([
        api.get("/subscription/plan"),
        api.get("/subscription/me"),
      ]);
      setPlan(p.data);
      setSub(m.data);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startTrial = async () => {
    setErr("");
    if (!agreed) {
      setErr("Please accept the auto-renewal consent and legal terms to continue.");
      return;
    }
    setBusy(true);
    try {
      const ok = await loadRazorpay();
      if (!ok) throw new Error("Could not load Razorpay. Check your internet.");
      const { data } = await api.post("/subscription/start");
      if (data.already_active) {
        await load();
        return;
      }
      const rzp = new window.Razorpay({
        key: data.key_id,
        subscription_id: data.subscription_id,
        name: "Breakout Scanner",
        description: `₹${(data.trial_paise / 100).toFixed(0)} trial · then ₹${(data.monthly_paise / 100).toFixed(0)}/month`,
        theme: { color: "#10b981" },
        prefill: { email: user?.email, name: user?.name },
        handler: async () => { await load(); },
        modal: { ondismiss: () => setBusy(false) },
      });
      rzp.on("payment.failed", (resp) => {
        setErr(resp?.error?.description || "Payment failed");
        setBusy(false);
      });
      rzp.open();
    } catch (e) {
      // Cloudflare-wrapped 502 responses aren't JSON, so `e.response?.data?.detail`
      // is undefined. Surface a helpful message instead of raw HTML or "Request failed".
      const raw = e.response?.data?.detail || e.message || "";
      const status = e.response?.status;
      let msg;
      if (typeof raw === "string" && raw.toLowerCase().includes("razorpay error")) {
        msg = "Payment gateway is temporarily unavailable. Our team has been notified — please try again in a few minutes, or contact breakoutscanner1987@gmail.com if this persists.";
      } else if (status === 502 || status === 503 || status === 504) {
        msg = "The payment service is currently unreachable. Please try again in a minute.";
      } else if (typeof raw === "string" && raw.startsWith("<")) {
        msg = "Something went wrong on our side. Please try again — if this keeps happening, email breakoutscanner1987@gmail.com.";
      } else {
        msg = raw || "Something went wrong. Please try again.";
      }
      setErr(msg);
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    if (!window.confirm("Cancel your subscription at the end of the current cycle? You keep access until then.")) return;
    setBusy(true);
    try {
      await api.post("/subscription/cancel");
      await load();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-app flex items-center justify-center">
        <CircleNotch className="animate-spin text-mod" size={20} weight="bold" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-app grain" data-testid="pricing-page">
      <Seo
        title="Pricing — ₹5 trial, then ₹299/month — Breakout Scanner"
        description="Premium NSE breakout signals: daily 6M + 3Y high probability picks with auto Entry / SL / Target. ₹5 for 5 days, then ₹299/month. Cancel anytime."
        path="/pricing"
      />
      <IndexStrip />
      <div className="py-10 px-6">
        <div className="max-w-4xl mx-auto">
          <button
            type="button"
            onClick={() => nav("/dashboard")}
            data-testid="pricing-back"
            className="mb-6 flex items-center gap-2 text-muted-fg hover:text-primary-fg text-sm font-mono uppercase tracking-widest"
          >
            <ArrowLeft size={14} /> Back to dashboard
          </button>

          <PricingHeader />

          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <PricingCard
              plan={plan}
              isActive={sub?.is_premium}
              status={sub?.subscription?.status}
              agreed={agreed}
              setAgreed={setAgreed}
              busy={busy}
              err={err}
              onStartTrial={startTrial}
              onCancel={cancel}
            />
          </motion.div>
        </div>
      </div>
    </div>
  );
}
